import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Building2, CalendarSearch, Clock, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isKioskSessionActive } from '../../../../../domains/flexOffice/kiosk/kioskSession.js';

import useBookingData from './hooks/useBookingData';
import useRecurrence from './hooks/useRecurrence';
import useAvailability from './hooks/useAvailability';
import usePricing from './hooks/usePricing';
import useBookingFlow from './hooks/useBookingFlow';

import LoginModal from './components/LoginModal';
import BookingSuccessBanner from './components/BookingSuccessBanner';
import ProductImage from './components/ProductImage';
import ProductDescription from './components/ProductDescription';
import RecurrenceButton from './components/RecurrenceButton';
import ReviewScheduleButton from './components/ReviewScheduleButton';
import BookingDetailsCard from './components/BookingDetailsCard';
import { DiscountCheckIndicator } from './components/LoadingIndicator';
import AvailabilityStatus from './components/AvailabilityStatus';
import BookingSummary from '../components/BookingSummary';
import RecurrenceModal from '../recurrence/RecurrenceModal';

import {
  formatTime12Hour,
  formatSelectedTimeRange,
  formatDate,
  getActualEndTime
} from './utils/dateTimeUtils';

const AvailabilityLoadingBanner = ({ message, onCancel }) => (
  <AnimatePresence>
    <motion.div
      key="avail-loading-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}
    >
      <motion.div
        initial={{ scale: 0.88, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.88, opacity: 0, y: 16 }}
        transition={{ type: 'spring', damping: 22, stiffness: 300 }}
        className="bg-white rounded-2xl shadow-2xl flex flex-col items-center mx-4 overflow-hidden"
        style={{ maxWidth: 400, width: '100%' }}
      >
        <div className="w-full bg-gradient-to-r from-blue-600 to-blue-500 px-6 pt-5 pb-6 flex flex-col items-center gap-3 relative">

          <motion.button
            whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.25)' }}
            whileTap={{ scale: 0.95 }}
            onClick={onCancel}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            aria-label="Cancel availability check"
          >
            <X className="w-4 h-4" />
          </motion.button>

          <div className="flex items-center gap-3 mt-1">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <CalendarSearch className="w-5 h-5 text-white" />
            </div>

            <div className="relative flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-white animate-spin" />
              </div>
              <span
                className="absolute inset-0 rounded-full animate-ping"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              />
            </div>

            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
          </div>

          <p className="text-white font-bold text-lg leading-snug text-center">
            Checking Availability
          </p>
        </div>

        <div className="px-8 py-5 flex flex-col items-center gap-4 w-full">
          <p className="text-slate-500 text-sm leading-relaxed text-center">
            {message}
          </p>

          <div className="flex items-center gap-2">
            {[0, 1, 2, 3].map((i) => (
              <motion.span
                key={i}
                className="block w-2 h-2 rounded-full bg-blue-500"
                animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }}
              />
            ))}
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onCancel}
            className="mt-1 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 hover:border-slate-300 transition-all"
          >
            <X className="w-4 h-4" />
            Cancel
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

const ReservationDetails = () => {
  const navigate = useNavigate();
  // This page is reused, unmodified, as the kiosk's product-details screen
  // (see KIOSK_ALLOWED_PATH_PREFIXES) — the large lg:pt-[12vh] top padding
  // and generous section gaps below were tuned for a scrollable desktop
  // page, but on a kiosk's fixed touchscreen (no natural scroll affordance)
  // they read as dead space above the fold. Only these spacing values
  // change when a kiosk session is active; every class name and pixel value
  // for a normal (non-kiosk) visit is byte-for-byte unchanged.
  const isKiosk = isKioskSessionActive();

  const {
    product,
    searchCriteria,
    selectedTime,
    initialRecurrenceData,
    initialUpdatedTimes,
    initialOccurrenceDates,
    bookingConfirmed,
    companyPrimaryId,
    userAccount,
    companyState,
    isAuthenticated,
    guestAccountId,
    accountIdforPostMessage,
    dispatch
  } = useBookingData();

  const [localUpdatedTimes] = useState(initialUpdatedTimes);
  const [showBookingSuccess, setShowBookingSuccess] = useState(bookingConfirmed);

  const workingDays = companyState?.workingSchedule?.workingDays || [];
  const [showAvailabilityLoader, setShowAvailabilityLoader] = useState(false);
  const recurrence = useRecurrence(searchCriteria, initialRecurrenceData, initialOccurrenceDates, workingDays);
  const availability = useAvailability(product, searchCriteria, selectedTime, companyState, companyPrimaryId, dispatch);
  const {
    autoNavigateToReview,
    availabilityData,
    isCheckingAvailability,
    setAutoNavigateToReview,
  } = availability;
  const pricing = usePricing(product, selectedTime, recurrence.occurrenceDates, localUpdatedTimes, searchCriteria);
  const bookingFlow = useBookingFlow(
    product,
    searchCriteria,
    selectedTime,
    companyState,
    companyPrimaryId,
    userAccount,
    isAuthenticated,
    guestAccountId,
    accountIdforPostMessage,
    dispatch,
    navigate
  );

  const handleViewSchedule = useCallback(() => {
    const datesToUse = recurrence.occurrenceDates.length > 0
      ? recurrence.occurrenceDates
      : recurrence.generateOccurrenceDates(recurrence.recurrenceData);

    navigate('/review-schedule', {
      state: {
        product,
        searchCriteria,
        selectedTime,
        recurrenceData: recurrence.recurrenceData,
        occurrenceDates: datesToUse,
        totalOccurrences: datesToUse.length,
        availabilityData,
        returnTo: 'selected-product-details',
        originalProductData: { product, searchCriteria, selectedTime, recurrenceData: recurrence.recurrenceData },
        updatedTimes: localUpdatedTimes
      }
    });
  }, [
    availabilityData,
    localUpdatedTimes,
    navigate,
    product,
    recurrence,
    searchCriteria,
    selectedTime,
  ]);

  useEffect(() => {
    if (showBookingSuccess) {
      const timer = setTimeout(() => setShowBookingSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showBookingSuccess]);

  useEffect(() => {
    if (autoNavigateToReview && !isCheckingAvailability && availabilityData) {
      handleViewSchedule();
      setAutoNavigateToReview(false);
    }
  }, [autoNavigateToReview, availabilityData, handleViewSchedule, isCheckingAvailability, setAutoNavigateToReview]);

  useEffect(() => {
    if (isCheckingAvailability) {
      setShowAvailabilityLoader(true);
    } else {
      setShowAvailabilityLoader(false);
    }
  }, [isCheckingAvailability]);

  const handleRecurrenceConfirm = async (recurrenceData) => {
    if (recurrenceData) {
      recurrence.setRecurrenceData(recurrenceData);
      recurrence.setShowRecurrenceModal(false);
      const newOccurrenceDates = recurrence.generateOccurrenceDates(recurrenceData);
      recurrence.setOccurrenceDates(newOccurrenceDates);
      await availability.checkRecurrenceAvailability(recurrenceData, newOccurrenceDates, true);
    } else {
      recurrence.setShowRecurrenceModal(false);
    }
  };

  const handleBackToResults = () => {
    navigate('/flex-home', {
      state: {
        view: 'results',
        searchCriteria,
        selectedCategoryId: product?.category?.primaryId
      }
    });
  };

  const handleSaveSchedule = () => {
    bookingFlow.handleSaveSchedule(
      recurrence.occurrenceDates,
      recurrence.recurrenceData,
      availability.availabilityData,
      localUpdatedTimes,
      pricing.getQuantityForDate,
      pricing.pricePerHour,
      () => getActualEndTime(selectedTime),
      pricing.setCheckingDiscounts
    );
  };

  const handleLoginRequired = () => {
    const bookingData = {
      product,
      searchCriteria,
      selectedTime,
      recurrenceData: recurrence.recurrenceData,
      occurrenceDates: recurrence.occurrenceDates.length > 0
        ? recurrence.occurrenceDates
        : recurrence.generateOccurrenceDates(recurrence.recurrenceData),
      availabilityData: availability.availabilityData,
      updatedTimes: localUpdatedTimes,
      totalPrice: pricing.totalPrice,
      duration: pricing.duration,
      pricePerHour: pricing.pricePerHour,
      totalOccurrences: recurrence.getTotalOccurrences(),
      timestamp: new Date().toISOString()
    };
    bookingFlow.handleLoginRequired(bookingData);
  };

  const handleNavigateToLogin = () => {
    const bookingData = {
      product,
      searchCriteria,
      selectedTime,
      recurrenceData: recurrence.recurrenceData,
      occurrenceDates: recurrence.occurrenceDates.length > 0
        ? recurrence.occurrenceDates
        : recurrence.generateOccurrenceDates(recurrence.recurrenceData),
      availabilityData: availability.availabilityData,
      updatedTimes: localUpdatedTimes
    };
    bookingFlow.handleNavigateToLogin(navigate, bookingData);
  };

  if (!product || !searchCriteria) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">No Product Selected</h2>
          <button
            onClick={() => navigate('/flex-home')}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const availabilityStatus = availability.getAvailabilityStatus(availability.availabilityData, recurrence.recurrenceData);

  const loadingMessage = availability.autoNavigateToReview
    ? 'Checking availability and preparing schedule…'
    : 'Checking availability for recurrence dates…';

const handleCancelAvailabilityCheck = () => {
  //  1. CLOSE UI IMMEDIATELY
  setShowAvailabilityLoader(false);

  //  2. STOP AUTO FLOW
  availability.setAutoNavigateToReview(false);

  //  3. CANCEL API IF SUPPORTED
  if (typeof availability.cancelCheck === 'function') {
    availability.cancelCheck();
  }

  //  4. RESET STATE (safe)
  recurrence.setRecurrenceData(null);
  recurrence.setOccurrenceDates([]);
};

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-8 px-4 fx-m-page ${isKiosk ? 'pt-4 sm:pt-6 lg:pt-6' : 'pt-6 sm:pt-8 lg:pt-[12vh]'}`}>

{showAvailabilityLoader && (        <AvailabilityLoadingBanner
          message={loadingMessage}
          onCancel={handleCancelAvailabilityCheck}
        />
      )}

      <div className="max-w-6xl mx-auto">
        <AnimatePresence>
          <LoginModal
            isOpen={bookingFlow.showLoginModal}
            onClose={bookingFlow.handleCloseLoginModal}
            onLogin={handleNavigateToLogin}
          />
        </AnimatePresence>

        <AnimatePresence>
          <BookingSuccessBanner
            show={showBookingSuccess}
            totalOccurrences={recurrence.getTotalOccurrences()}
          />
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="bg-white shadow-xl overflow-hidden border border-slate-200"
          style={{ borderRadius: '20px' }}
        >
          <div className={isKiosk ? 'p-4 sm:p-5 lg:p-6' : 'p-4 sm:p-6 lg:p-8'}>
            <motion.button
              whileHover={{ x: -3 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleBackToResults}
              disabled={bookingFlow.isProcessing}
              className={`flex items-center text-slate-600 hover:text-slate-900 font-medium transition-colors px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl hover:bg-slate-50 border border-slate-200 ${isKiosk ? 'mb-4' : 'mb-8'}`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Results
            </motion.button>

            
            <div className={`flex flex-col lg:grid lg:grid-cols-2 lg:items-start ${isKiosk ? 'gap-3 lg:gap-x-6 lg:gap-y-3' : 'gap-4 lg:gap-x-10 lg:gap-y-6'}`}>
              <div className={`contents lg:flex lg:flex-col ${isKiosk ? 'lg:gap-3' : 'lg:gap-6'}`}>
                <div className="order-1 lg:order-1">
                  <ProductImage product={product} isKiosk={isKiosk} />
                </div>

                {availabilityStatus && !availability.isCheckingAvailability && (
                  <div className="order-4 lg:order-5">
                    <AvailabilityStatus
                      status={availabilityStatus}
                      onViewSchedule={handleViewSchedule}
                    />
                  </div>
                )}

                <motion.div className="order-6 lg:order-3 space-y-4">
                  <RecurrenceButton
                    hasRecurrence={!!recurrence.recurrenceData}
                    displayText={recurrence.getRecurrenceDisplayText()}
                    onClick={() => recurrence.setShowRecurrenceModal(true)}
                    disabled={bookingFlow.isProcessing || availability.isCheckingAvailability}
                  />

                  {recurrence.recurrenceData && (
                    <ReviewScheduleButton
                      onClick={handleViewSchedule}
                      disabled={bookingFlow.isProcessing || availability.isCheckingAvailability}
                      totalOccurrences={recurrence.getTotalOccurrences()}
                    />
                  )}
                </motion.div>

                {pricing.checkingDiscounts && (
                  <div className="order-7 lg:order-4">
                    <DiscountCheckIndicator />
                  </div>
                )}

                <div className="order-8 lg:order-2">
                  <ProductDescription description={product.description} />
                </div>
              </div>

              <div className={`contents lg:flex lg:flex-col ${isKiosk ? 'lg:gap-3' : 'lg:gap-6'}`}>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="order-2 lg:order-1"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{product.name}</h1>
                  </div>
                  <p className="text-slate-600 flex items-center text-sm mb-3">
                    <Building2 className="w-4 h-4 mr-1.5" />
                    {product.category?.name || 'Premium workspace'}
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="order-3 lg:order-2"
                >
                  <BookingDetailsCard
                    date={formatDate(searchCriteria?.date)}
                    timeRange={formatSelectedTimeRange(selectedTime, formatTime12Hour)}
                    duration={pricing.duration}
                    people={searchCriteria?.people}
                    recurrenceData={recurrence.recurrenceData}
                    recurrenceDisplayText={recurrence.getRecurrenceDisplayText()}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="order-5 lg:order-3 pt-4 border-t border-slate-200 bg-white fx-m-sticky-cta"
                >
                  <BookingSummary
                    totalPrice={pricing.totalPrice}
                    duration={pricing.duration}
                    pricePerHour={pricing.pricePerHour}
                    totalOccurrences={recurrence.getTotalOccurrences()}
                    hasRecurrence={!!recurrence.recurrenceData}
                    isProcessing={bookingFlow.isProcessing || availability.isCheckingAvailability}
                    isCheckingAvailability={availability.isCheckingAvailability}
                    processingProgress={bookingFlow.processingProgress}
                    onConfirm={handleSaveSchedule}
                    onLoginRequired={handleLoginRequired}
                    availabilityStatus={availabilityStatus}
                    onViewSchedule={handleViewSchedule}
                  />
                </motion.div>

                {bookingFlow.error && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="order-9 lg:order-4 p-4 bg-red-50 border border-red-200 rounded-xl"
                  >
                    <div className="text-red-700 text-sm font-medium">{bookingFlow.error}</div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          <RecurrenceModal
            isOpen={recurrence.showRecurrenceModal}
            onClose={() => recurrence.setShowRecurrenceModal(false)}
            selectedDate={searchCriteria.date}
            selectedTime={selectedTime}
            onRecurrenceChange={handleRecurrenceConfirm}
            existingRecurrenceData={recurrence.recurrenceData}
            workingDays={workingDays}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default ReservationDetails;
